'use client';
import {observer} from 'mobx-react';
import {useEffect, useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {useTranslation} from '@/app/i18n/client';
import type INotification from '@/models/Notifications';
import {NotificationType} from '@/models/Notifications';
import type NotificationStore from '@/store/NotificationStore';
import GlobalStoresIdentifier from '@/utils/enums/store-identifier';
import {useGlobalStoreContext} from '@/utils/hooks/contextHooks';

import type {ButtonProps} from './base/Button';
import Button from './base/Button';
import Typography from './base/Typography';
import NotificationIcon from './NotificationIcon';

interface NotificationPropType extends INotification {
  locale: string;
}

function Notification({locale, id, msg, type, disableAuto, actions = [], subMsg, onClose}: NotificationPropType) {
  const store = useGlobalStoreContext();
  const {t, i18n} = useTranslation(locale, 'common');
  const {removeNotification} = store.getStore<NotificationStore>(GlobalStoresIdentifier.NOTIFICATION_STORE);

  const notificationIcon = useMemo(() => {
    return <NotificationIcon type={type} />;
  }, [type]);

  const handleClose = () => {
    if (id) {
      removeNotification(id);
    }
  };

  const notificationActions: (ButtonProps & {label: string})[] = [
    {
      className: 'font-bold px-5 py-3.5 text-button-text min-w-[unset] enabled:hover:bg-inherit',
      variant: 'none',
      onClick: () => {
        handleClose();
        onClose?.();
      },
      label: t('btns.close'),
    },
    ...actions,
  ];

  useEffect(() => {
    if (!disableAuto) {
      setTimeout(() => {
        if (id) removeNotification(id);
      }, 5000);
    }
  }, [disableAuto, id, removeNotification]);

  return (
    <div
      key={id}
      className="flex h-14 w-[600px] items-center justify-between rounded-lg border border-base-color bg-base-900 py-0 shadow-md">
      <div className=" flex items-center px-4 py-[5.5px] text-green-bright">
        {typeof msg === 'string' ? (
          <div className="flex flex-col ">
            <div className=" flex items-center gap-1">
              {notificationIcon}
              <Typography
                className={twMerge('text-sm', type === NotificationType.WARNING ? 'text-red-bright' : 'text-green-bright')}
                mode="bolder">
                {i18n.exists(`common:errors.${msg}`) ? t(`errors.${msg}`) : msg}
              </Typography>
            </div>
            {subMsg && (
              <Typography className="pl-5 text-xs text-base-500">
                {i18n.exists(`common:errors.${subMsg}`) ? t(`errors.${subMsg}`) : subMsg}
              </Typography>
            )}
          </div>
        ) : (
          msg
        )}
      </div>
      <div className="flex h-full w-[94px] flex-col items-center justify-center border-l">
        {notificationActions.map((item, index) => {
          return (
            <Button
              key={item.label}
              className={`flex h-1/2 w-full items-center justify-center border-l border-l-base-200 text-xs font-bold text-button-text enabled:hover:bg-inherit ${
                index !== notificationActions.length - 1 && 'border-b border-b-base-200 '
              }`}
              variant="none"
              onClick={(e) => {
                handleClose();
                item.onClick?.(e);
              }}>
              {item.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
export default observer(Notification);
